import React, { useRef, ReactNode } from 'react';
import Link from 'next/link';
import cn from 'classnames';
// Components
import CardDescription from '../CardDescription/CardDescription';
import CardVideo from './components/CardVideo';
// UI
import Image from 'ui3/Image/Image';
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
// Styles
import style from './BaseCard.module.scss';

export type ContentType = 'interview' | 'stream' | 'image' | 'video';
export type ContentStatus =
  | 'hidden'
  | 'cancelled'
  | 'live'
  | 'upcoming'
  | 'past'
  | 'weekly';
export type CardSize = 'small' | 'medium' | 'large';

export interface BasicBaseCardProps {
  size?: CardSize;
  title?: string;
  user?: string;
  dateTime?: string; // TODO convert this to dayjs and have 2 formats for description and label
  imageUrl: string;
  hoverImageUrl?: string;
  linkUrl?: string;
  userUrl?: string;
  isPlay?: boolean;
  isProductCard?: boolean;
  isArticleCard?: boolean;
  topLabels?: React.ReactNode;
  bottomLabels?: React.ReactNode;
  descriptionButton?: React.ReactNode;
  grayButtonHoverStyle?: boolean;
  className?: string;
  descriptionClassName?: string;
  imageWrapperClassname?: string;
  videoAttributes?: {
    videoURL?: string;
    videoId: string;
    playInModal?: boolean;
  };
  priorityImageLoading?: boolean;
  showTitle?: boolean;
  userLineClamp?: number;
  titleMaxLength?: number;
  onLinkClick?: (
    e?:
      | React.MouseEvent<HTMLAnchorElement>
      | React.KeyboardEvent<HTMLAnchorElement>
  ) => void;
}

export type BaseCardProps = React.ComponentPropsWithoutRef<'img'> &
  BasicBaseCardProps;

const WithLink = ({
  link,
  onClick,
  children,
}: {
  link?: string;
  onClick?: (
    e?:
      | React.MouseEvent<HTMLAnchorElement>
      | React.KeyboardEvent<HTMLAnchorElement>
  ) => void;
  children: ReactNode;
}) =>
  link ? (
    <Link href={link} prefetch={false}>
      <a
        href={link}
        {...(onClick && { onClick: onClick, onKeyPress: onClick })}
      >
        {children}
      </a>
    </Link>
  ) : (
    <>{children}</>
  );

const BaseCard: React.FC<BaseCardProps> = ({
  size,
  title,
  user,
  dateTime,
  imageUrl,
  hoverImageUrl,
  linkUrl,
  userUrl,
  isPlay,
  isProductCard,
  isArticleCard,
  topLabels,
  bottomLabels,
  descriptionButton,
  grayButtonHoverStyle,
  width,
  height,
  className,
  descriptionClassName,
  imageWrapperClassname,
  videoAttributes,
  priorityImageLoading,
  showTitle = true,
  userLineClamp = 2,
  titleMaxLength,
  onLinkClick,
}) => {
  const imageWrapperRef = useRef<HTMLDivElement | null>(null);
  const sizeClass = size ?? 'large';

  const handleLinkClick = (
    e:
      | React.MouseEvent<HTMLAnchorElement>
      | React.KeyboardEvent<HTMLAnchorElement>
  ) => {
    onLinkClick?.(e);
  };

  const imageTitle = title ? `${title} on MILLIONS` : undefined;

  return (
    <div
      className={cn(
        'baseCardRoot', // Set specific class name for CarouselSlider
        style.root,
        {
          [style[sizeClass]]: size && !width && !height,
          [style.articleCard]: isArticleCard,
          [style.productCard]: isProductCard,
        },
        className
      )}
    >
      <div
        className={cn(style.imageContainer, {
          [style[sizeClass]]: size && !width && !height,
        })}
        style={{ ...(width && { width }), ...(height && { height }) }}
      >
        <WithLink link={linkUrl} onClick={handleLinkClick}>
          <div
            className={cn(
              style.imageWrapper,
              {
                [style.articleCard]: isArticleCard,
                [style.hasHoverImage]: hoverImageUrl,
              },
              imageWrapperClassname
            )}
            ref={imageWrapperRef}
          >
            {videoAttributes ? (
              <CardVideo
                videoThumbnailURL={imageUrl}
                videoURL={videoAttributes.videoURL}
                videoId={videoAttributes.videoId}
                title={title}
                priorityImageLoading={priorityImageLoading}
                imageWrapperRef={imageWrapperRef}
                hasRedirect={!!linkUrl}
                playInModal={videoAttributes.playInModal}
              />
            ) : (
              <>
                <Image
                  src={imageUrl}
                  alt={title ?? ''}
                  title={imageTitle}
                  // ssrFill: native CDN <img> keeps thumbs visible to
                  // crawlers/HTML previews (next/image's SSR shim is a 1×1
                  // GIF); priority only controls eager vs lazy.
                  ssrFill
                  priority={Boolean(priorityImageLoading)}
                  sizes="(max-width: 344px) 50vw, (max-width: 344px) 33vw, 20vw"
                  className={style.mainImage}
                />
                {hoverImageUrl && (
                  <Image
                    src={hoverImageUrl}
                    alt={title ?? ''}
                    title={imageTitle}
                    ssrFill
                    priority={Boolean(priorityImageLoading)}
                    sizes="(max-width: 344px) 50vw, (max-width: 344px) 33vw, 20vw"
                    className={style.hoverImage}
                  />
                )}
              </>
            )}
          </div>
        </WithLink>

        {/* Top labels */}
        {topLabels ? (
          <div
            className={cn(style.labelWrapper, {
              [style.productCard]: isProductCard,
              [style[sizeClass]]: size && !width && !height,
            })}
          >
            {topLabels}
          </div>
        ) : null}
        {/* Play icon */}
        {isPlay && (
          <MillionsIcon
            name="playTransparentOutlined"
            size={60}
            className={style.playIconOverlay}
          />
        )}
        {/* Bottom labels */}
        {bottomLabels ? (
          <div
            className={cn(style.labelWrapper, style.bottom, {
              [style[sizeClass]]: size && !width && !height,
            })}
          >
            {bottomLabels}
          </div>
        ) : null}
      </div>
      <div className={descriptionClassName}>
        <CardDescription
          className={cn(style.cardDescription, {
            [style[sizeClass]]: size && !width && !height,
          })}
          title={title}
          user={user}
          userUrl={userUrl}
          date={dateTime}
          size={size}
          showDate={!isProductCard}
          showTitle={showTitle}
          userLineClamp={userLineClamp}
          titleMaxLength={titleMaxLength}
        >
          {descriptionButton && (
            <div
              className={cn(style.descriptionButtonWrapper, {
                [style.descriptionButtonWrapperGrayHover]: grayButtonHoverStyle,
              })}
            >
              {descriptionButton}
            </div>
          )}
        </CardDescription>
      </div>
    </div>
  );
};

export default BaseCard;
